// ─────────────────────────────────────────────────────────────────────────────
// Conjugarden — a language-ecosystem roguelite layered on the conjugation engine.
//
// You run Expeditions through tense-themed biomes, answering real exercises.
// Correct answers grow a bloom streak and earn spores; misses cost vitality.
// Survive a biome to bank Pollen (meta-currency) in the Greenhouse, where you
// buy permanent unlocks. Mastered verbs become living specimens in your garden.
//
// This module is pure state + data + helpers. All run-time UI lives in the page.
// State is persisted per-language so each garden grows independently.
// ─────────────────────────────────────────────────────────────────────────────
import type { ConjTense } from './conjugation.js'

export type BiomeId = 'meadow' | 'riverlands' | 'highlands' | 'mistwood' | 'canopy'
export type TraitId =
  | 'deeproots' | 'pollinator' | 'nitrogen' | 'thornback'
  | 'mycelium' | 'photosynthesis' | 'perennial' | 'resilientbark'
  | 'symbiosis' | 'mycorrhiza' | 'apex' | 'bloomcaller'
  | 'compostheart' | 'evergreen' | 'nectarfont' | 'seedbank'
export type TraitRarity = 'common' | 'rare' | 'legendary'

// ── Biomes ───────────────────────────────────────────────────────────────────
// Each biome is a band of tenses. Meadow is free; the rest are unlocked with Pollen.
// `tenses` are intersected with what a language actually supports at run time.
export type Biome = {
  id: BiomeId
  name: string
  flavor: string
  tenses: ConjTense[]
  cost: number          // pollen to unlock (0 = always available)
  glyph: string         // emoji marker used in lists
  hue: 'leaf' | 'water' | 'wind' | 'mist' | 'sun'
}

export const BIOMES: Biome[] = [
  { id: 'meadow',     name: 'Sunmeadow',     flavor: 'Open present-tense grassland. Where every garden begins.', tenses: ['present'],                                  cost: 0,   glyph: '🌱', hue: 'sun'   },
  { id: 'riverlands', name: 'Riverlands',    flavor: 'Braided channels of the past — preterite currents, imperfect pools.', tenses: ['preterite', 'imperfect'],          cost: 140, glyph: '🌾', hue: 'water' },
  { id: 'highlands',  name: 'Windhighlands', flavor: 'Exposed ridges of what will be and what could be.', tenses: ['future', 'conditional'],                          cost: 220, glyph: '🌲', hue: 'wind'  },
  { id: 'mistwood',   name: 'Mistwood',      flavor: 'A subjunctive fog where nothing is certain.', tenses: ['present_subjunctive', 'imperfect_subjunctive'],         cost: 340, glyph: '🍄', hue: 'mist'  },
  { id: 'canopy',     name: 'Highcanopy',    flavor: 'The compound canopy — perfect tenses layered overhead.', tenses: ['present_perfect', 'pluperfect'],             cost: 480, glyph: '🌳', hue: 'leaf'  },
]

export function biomeById(id: BiomeId): Biome {
  return BIOMES.find(b => b.id === id) ?? BIOMES[0]
}

// ── Traits (run-scoped relics) ───────────────────────────────────────────────
export type Trait = {
  id: TraitId
  name: string
  blurb: string
  glyph: string
  cost: number          // pollen to add to the draft pool (0 = always draftable)
  rarity: TraitRarity
}

export const TRAIT_RARITY_META: Record<TraitRarity, { color: string; bg: string; label: string }> = {
  common:    { color: 'var(--muted)',    bg: 'var(--surf2)',           label: 'Common'    },
  rare:      { color: '#B084E9',         bg: 'rgba(176,132,233,0.13)', label: 'Rare'      },
  legendary: { color: 'var(--accent)',   bg: 'var(--accent-bg)',       label: 'Legendary' },
}

export const TRAITS: Trait[] = [
  // ── common (always in the draft pool) ──
  { id: 'deeproots',      name: 'Deep Roots',      glyph: '🪵', rarity: 'common', blurb: 'The first miss in each grove costs no vitality.',          cost: 0   },
  { id: 'pollinator',     name: 'Pollinator',      glyph: '🐝', rarity: 'common', blurb: '+5 spores on every correct answer.',                        cost: 0   },
  { id: 'nitrogen',       name: 'Nitrogen Fixer',  glyph: '🍃', rarity: 'common', blurb: 'Regular verbs grant +60% spores.',                          cost: 0   },
  { id: 'thornback',      name: 'Thornback',       glyph: '🌵', rarity: 'common', blurb: 'Irregulars grant +120% spores — but a missed irregular stings for 2 vitality.', cost: 0 },
  // ── unlockable utility ──
  { id: 'mycelium',       name: 'Mycelium Web',    glyph: '🕸️', rarity: 'common', blurb: 'A miss halves your bloom streak instead of breaking it.',  cost: 150 },
  { id: 'photosynthesis', name: 'Photosynthesis',  glyph: '☀️', rarity: 'common', blurb: 'Every 8 correct answers restore 1 vitality.',              cost: 190 },
  { id: 'perennial',      name: 'Perennial',       glyph: '🌷', rarity: 'common', blurb: 'Each grove begins with a +3 bloom streak.',                 cost: 160 },
  { id: 'resilientbark',  name: 'Resilient Bark',  glyph: '🛡️', rarity: 'common', blurb: 'Begin the expedition with +1 vitality.',                   cost: 140 },
  // ── rare (build-shaping) ──
  { id: 'apex',           name: 'Apex Bloom',      glyph: '⚡', rarity: 'rare',   blurb: 'Every 3rd correct answer is a critical bloom — double spores.', cost: 200 },
  { id: 'bloomcaller',    name: 'Bloomcaller',     glyph: '🌻', rarity: 'rare',   blurb: 'Your bloom streak can climb to ×4 spores (up from ×3).',     cost: 220 },
  { id: 'compostheart',   name: 'Compost Heart',   glyph: '♻️', rarity: 'rare',   blurb: 'Even a wrong answer composts into half spores.',             cost: 180 },
  { id: 'evergreen',      name: 'Evergreen',       glyph: '🌲', rarity: 'rare',   blurb: 'Begin the expedition with +2 max vitality, fully grown.',    cost: 200 },
  { id: 'nectarfont',     name: 'Nectar Font',     glyph: '🍯', rarity: 'rare',   blurb: 'Wild-word discovery bonuses are doubled.',                   cost: 170 },
  // ── legendary (run-defining engines) ──
  { id: 'symbiosis',      name: 'Symbiosis',       glyph: '🌿', rarity: 'legendary', blurb: '+3 spores per correct for every OTHER adaptation you hold.', cost: 280 },
  { id: 'mycorrhiza',     name: 'Mycorrhiza',      glyph: '🍄', rarity: 'legendary', blurb: 'Each Legendary word you discover raises this run’s max vitality by 1.', cost: 300 },
  { id: 'seedbank',       name: 'Seedbank',        glyph: '🌰', rarity: 'legendary', blurb: 'Growing a specimen also restores 1 vitality.',           cost: 260 },
]

export function traitById(id: TraitId): Trait {
  return TRAITS.find(t => t.id === id) ?? TRAITS[0]
}

// ── Persistent state ─────────────────────────────────────────────────────────
export type Specimen = {
  verb: string
  correct: number       // correct answers accumulated in garden mode
  mastered: boolean     // grown into a permanent plant
  biome: BiomeId        // where it was first grown
  bornAt: number        // timestamp of mastery
}

// ── Species discovery (the "Pokédex" layer) ─────────────────────────────────
// Meeting a verb you've never seen mid-expedition is a framed event: a silenced
// word stirs in the Hush. You catalogue it (discovered) and, once mastered,
// catch it (it becomes a living specimen).
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

export type DiscoveredSpecies = {
  verb: string
  rarity: Rarity
  biome: BiomeId
  firstSeen: number
}

export const RARITY_META: Record<Rarity, { label: string; color: string; bg: string; bonus: number; order: number; sigil: string }> = {
  common:    { label: 'Common',    color: 'var(--muted)',    bg: 'var(--surf2)',     bonus: 8,   order: 0, sigil: '·'  },
  uncommon:  { label: 'Uncommon',  color: 'var(--blue)',     bg: 'var(--blue-bg)',   bonus: 15,  order: 1, sigil: '◇'  },
  rare:      { label: 'Rare',      color: '#B084E9',         bg: 'rgba(176,132,233,0.13)', bonus: 30, order: 2, sigil: '◆' },
  epic:      { label: 'Epic',      color: 'var(--accent-2)', bg: 'rgba(224,85,32,0.13)', bonus: 60, order: 3, sigil: '★' },
  legendary: { label: 'Legendary', color: 'var(--accent)',   bg: 'var(--accent-bg)', bonus: 120, order: 4, sigil: '✦' },
}

const LEGENDARY_VERBS: Record<string, string[]> = {
  es: ['ser', 'estar', 'ir', 'haber', 'tener', 'hacer', 'poder', 'decir', 'ver', 'dar', 'saber', 'querer'],
  pt: ['ser', 'estar', 'ir', 'haver', 'ter', 'fazer', 'poder', 'dizer', 'ver', 'dar', 'saber', 'querer'],
  fi: ['olla', 'tehdä', 'mennä', 'tulla', 'voida', 'saada', 'nähdä', 'antaa', 'tietää', 'käydä'],
}

// Deterministic rarity: legendaries are curated, irregulars are rare/epic,
// regulars are common/uncommon. Stable per verb so a species never changes tier.
export function rarityOf(verb: string, verbType: string, langCode: string): Rarity {
  const v = verb.toLowerCase()
  if ((LEGENDARY_VERBS[langCode] ?? LEGENDARY_VERBS.es).includes(v)) return 'legendary'
  const h = hashString(verb)
  if (verbType === 'Irregular') return h % 3 === 0 ? 'epic' : 'rare'
  return h % 4 === 0 ? 'uncommon' : 'common'
}

export type GardenState = {
  version: number
  pollen: number
  maxVitality: number
  pollenMultiplier: number     // compost upgrades (1.0 base)
  unlockedBiomes: BiomeId[]
  unlockedTraits: TraitId[]    // traits added to the draft pool via the shop
  specimens: Record<string, Specimen>
  discovered: Record<string, DiscoveredSpecies>   // verbs encountered in the wild
  clears: Partial<Record<BiomeId, number>>
  depth: Partial<Record<BiomeId, number>>         // highest Hush-depth cleared per biome
  heartwood: number                               // prestige currency (boss/deep rewards)
  oldGrowth: Record<string, number>               // owned tiers of scaling Old-Growth upgrades
  bossClears: Partial<Record<BiomeId, number>>
  stats: { runs: number; bestDepth: number; totalCorrect: number; totalPollen: number; withered: number; bestStreak: number; wins: number; bosses: number; reviewFixed: number }
  seenBeats: string[]          // story beats already shown
  seenAchievements: string[]   // achievements already celebrated
}

const VERSION = 1
const keyFor = (lang: string) => `c8-conjugarden-v${VERSION}:${lang}`

export const MASTERY_THRESHOLD = 3     // correct answers to grow a specimen
export const VITALITY_TIERS = [3, 4, 5, 6]
export const VITALITY_COSTS = [0, 200, 380, 620]    // cost to reach tier index
export const COMPOST_TIERS = [1.0, 1.15, 1.3, 1.5]
export const COMPOST_COSTS = [0, 160, 320, 560]

// ── Run shape & bosses (Phase 1) ─────────────────────────────────────────────
export const NORMAL_GROVES = 3                      // then a boss grove
export const GROVE_COUNT = NORMAL_GROVES + 1
export const BOSS_BASE_TIMER = 45                   // seconds at depth 0
export const BOSS_BASE_CARDS = 7
export const BOSS_POLLEN = 150
export const HEARTWOOD_PER_BOSS = 2

// ── Depth of the Hush (Phase 2 — the Ascension/Heat dial) ────────────────────
export type DepthConfig = {
  depth: number
  startVitalityDelta: number
  sporeMult: number
  missPenaltyBonus: number    // extra vitality lost per miss
  bossTimer: number
  irregularBias: number       // 0..1, chance a normal card is forced irregular
  extraBossCards: number
  heartwood: boolean          // does this depth pay Heartwood at all?
}
export function depthConfig(depth: number): DepthConfig {
  const d = Math.max(0, Math.floor(depth))
  return {
    depth: d,
    startVitalityDelta: -Math.floor(d / 2),
    sporeMult: +(1 + d * 0.3).toFixed(2),
    missPenaltyBonus: d >= 3 ? 1 : 0,
    bossTimer: Math.max(20, BOSS_BASE_TIMER - d * 4),
    irregularBias: Math.min(0.85, d * 0.15),
    extraBossCards: Math.floor(d / 2),
    heartwood: d >= 2,
  }
}
export const MAX_DEPTH = 8

// ── Old Growth: the infinite, scaling prestige sink (Phase 4) ────────────────
export type OldGrowthItem = { id: string; name: string; glyph: string; desc: string; baseCost: number; growth: number; maxTier?: number }
export const OLD_GROWTH: OldGrowthItem[] = [
  { id: 'heartvitality', name: 'Heartwood Vigor',  glyph: '💚', desc: '+1 starting vitality, permanently.',          baseCost: 4, growth: 1.8, maxTier: 4 },
  { id: 'richsoil',      name: 'Deep Loam',        glyph: '🟤', desc: '+10% Pollen from every expedition.',          baseCost: 3, growth: 1.6 },
  { id: 'combocrown',    name: 'Bloom Crown',      glyph: '👑', desc: '+0.5 to your maximum bloom multiplier.',      baseCost: 5, growth: 2.0, maxTier: 4 },
  { id: 'reroll',        name: 'Drafting Wind',    glyph: '🍃', desc: '+1 adaptation reroll per expedition.',        baseCost: 4, growth: 1.7, maxTier: 3 },
]
export function oldGrowthCost(item: OldGrowthItem, owned: number): number {
  return Math.round(item.baseCost * Math.pow(item.growth, owned))
}

export function initialGarden(): GardenState {
  return {
    version: VERSION,
    pollen: 0,
    maxVitality: VITALITY_TIERS[0],
    pollenMultiplier: 1.0,
    unlockedBiomes: ['meadow'],
    unlockedTraits: TRAITS.filter(t => t.cost === 0).map(t => t.id),
    specimens: {},
    discovered: {},
    clears: {},
    depth: {},
    heartwood: 0,
    oldGrowth: {},
    bossClears: {},
    stats: { runs: 0, bestDepth: 0, totalCorrect: 0, totalPollen: 0, withered: 0, bestStreak: 0, wins: 0, bosses: 0, reviewFixed: 0 },
    seenBeats: [],
    seenAchievements: [],
  }
}

export function readGarden(lang: string): GardenState {
  if (typeof window === 'undefined') return initialGarden()
  try {
    const raw = window.localStorage.getItem(keyFor(lang))
    if (!raw) return initialGarden()
    const parsed = JSON.parse(raw) as Partial<GardenState>
    const base = initialGarden()
    // Merge defensively so older / partial saves don't crash.
    return {
      ...base,
      ...parsed,
      stats: { ...base.stats, ...(parsed.stats ?? {}) },
      specimens: parsed.specimens ?? {},
      discovered: parsed.discovered ?? {},
      clears: parsed.clears ?? {},
      depth: parsed.depth ?? {},
      heartwood: parsed.heartwood ?? 0,
      oldGrowth: parsed.oldGrowth ?? {},
      bossClears: parsed.bossClears ?? {},
      seenBeats: parsed.seenBeats ?? [],
      seenAchievements: parsed.seenAchievements ?? [],
      unlockedBiomes: parsed.unlockedBiomes?.length ? parsed.unlockedBiomes : base.unlockedBiomes,
      unlockedTraits: parsed.unlockedTraits?.length ? parsed.unlockedTraits : base.unlockedTraits,
    }
  } catch {
    return initialGarden()
  }
}

export function writeGarden(lang: string, state: GardenState, opts?: { silent?: boolean }): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(keyFor(lang), JSON.stringify(state))
    if (!opts?.silent) window.dispatchEvent(new CustomEvent('c8:garden-saved', { detail: { lang } }))
  } catch {}
}

export function resetGarden(lang: string): GardenState {
  const fresh = initialGarden()
  writeGarden(lang, fresh)
  return fresh
}

// ── Derived helpers ──────────────────────────────────────────────────────────
export function masteredSpecimens(state: GardenState): Specimen[] {
  return Object.values(state.specimens).filter(s => s.mastered)
}

export function availableBiomes(state: GardenState, supportedTenses: Set<ConjTense>): Biome[] {
  return BIOMES.filter(b => b.tenses.some(t => supportedTenses.has(t)))
}

export function isBiomeUnlocked(state: GardenState, id: BiomeId): boolean {
  return state.unlockedBiomes.includes(id)
}

// Combo multiplier grows in steps of 5 correct, capped (cap raised by traits/upgrades).
export function comboMultiplier(combo: number, cap = 3): number {
  return Math.min(cap, 1 + Math.floor(combo / 5) * 0.5)
}

// Garden "level" — a soft prestige derived from total specimens + biome clears.
export function gardenLevel(state: GardenState): { level: number; title: string; toNext: number; have: number; need: number } {
  const specimens = masteredSpecimens(state).length
  const clears = Object.values(state.clears).reduce((a, b) => a + (b ?? 0), 0)
  const score = specimens * 4 + clears * 6
  const titles = ['Bare Soil', 'First Sprouts', 'Tended Plot', 'Wild Patch', 'Verdant Garden', 'Living Grove', 'Ancient Ecosystem']
  // Thresholds grow super-linearly.
  let level = 0
  let lo = 0
  const step = (n: number) => n * 30 + n * n * 8
  for (let i = 1; i < 40; i++) {
    if (score >= step(i)) { level = i; lo = step(i) } else break
  }
  const need = step(level + 1)
  const title = titles[Math.min(level, titles.length - 1)]
  return { level, title, toNext: Math.max(0, need - score), have: score - lo, need: need - lo }
}

// Deterministic hash → used to give each specimen a stable visual form.
export function hashString(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0)
}
